import React, { useEffect, useState } from "react";
import { Star, Quote, BadgeCheck } from "lucide-react";
import { api } from "@/lib/api";

const SEED_REVIEWS = [
  { name: "Amit R.", city: "Mumbai", avatar: "AR", rating: 5, text: "Got clarity on my rent dispute in a 20-minute call. Adv. Priya was patient, explained every step in Hindi. Worth every rupee.", topic: "Property", date: "Mar 2026", highlight: "Worth every rupee" },
  { name: "Shilpa K.", city: "Delhi", avatar: "SK", rating: 5, text: "I was terrified after receiving a police notice. Advoset connected me with a criminal lawyer within minutes — the bail was granted the same week.", topic: "Criminal", date: "Feb 2026", highlight: "within minutes" },
  { name: "Ravi T.", city: "Bangalore", avatar: "RT", rating: 5, text: "The AI gave me a great starting summary for free. Paid only ₹299 for a quick call — saved me thousands in unnecessary legal fees.", topic: "Consumer", date: "Feb 2026", highlight: "Paid only ₹299" },
  { name: "Neha S.", city: "Pune", avatar: "NS", rating: 5, text: "Mutual divorce was confusing, but Adv. Kavita walked me through it step by step. No jargon, no judgement. Felt safe throughout.", topic: "Family", date: "Jan 2026", highlight: "No jargon, no judgement" },
  { name: "Imran Q.", city: "Hyderabad", avatar: "IQ", rating: 4, text: "Property title verification done before I paid my builder. Caught a major issue. Advoset's lawyer literally saved me from a fraud.", topic: "Property", date: "Jan 2026", highlight: "saved me from a fraud" },
  { name: "Deepa M.", city: "Chennai", avatar: "DM", rating: 5, text: "Filed a consumer case against a bad gadget seller. Got full refund in 3 months. Spent just ₹199 on the initial chat.", topic: "Consumer", date: "Dec 2025", highlight: "just ₹199" },
];

function initialsOf(name = "") {
  return name.split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("");
}

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-IN", { month: "short", year: "numeric" }); } catch { return ""; }
}

function avatarUrl(seed) {
  const s = encodeURIComponent(seed || "Advoset");
  return `https://api.dicebear.com/9.x/initials/svg?seed=${s}&backgroundColor=0A2540&textColor=D4AF37&fontWeight=700`;
}

function renderTextWithHighlight(text, highlight) {
  if (!highlight) return text;
  const idx = text.toLowerCase().indexOf(highlight.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <span className="bg-[#FBF4DF] text-[#8a6d14] font-bold px-1 rounded">{text.slice(idx, idx + highlight.length)}</span>
      {text.slice(idx + highlight.length)}
    </>
  );
}

export default function Testimonials() {
  const [reviews, setReviews] = useState(SEED_REVIEWS);

  useEffect(() => {
    api.get("/reviews", { params: { limit: 12 } })
      .then((r) => {
        const real = (r.data || []).map((x) => ({
          name: (x.user_name || "User").split(" ")[0] + ".",
          city: x.city || "",
          avatar: initialsOf(x.user_name || "User"),
          rating: x.rating,
          text: x.text || "",
          topic: x.topic || "Legal",
          date: formatDate(x.created_at),
          highlight: "",
        })).filter((x) => x.text && x.text.length > 20);
        if (real.length >= 3) setReviews(real);
      })
      .catch(() => {});
  }, []);

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-14 lg:pb-20" data-testid="testimonials-section">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <span className="text-xs font-bold uppercase tracking-widest text-[#D4AF37]">Real stories</span>
          <h2 className="mt-2 text-3xl lg:text-4xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>
            What real Indians say
          </h2>
          <p className="text-sm text-slate-500 mt-1">Verified Advoset users. First names only, to protect privacy.</p>
        </div>
        <div className="flex items-center gap-2 bg-amber-50 px-3 py-1.5 rounded-full">
          <div className="flex items-center gap-0.5 text-amber-500">
            {Array.from({ length: 5 }).map((_, i) => <Star key={i} size={13} fill="currentColor" />)}
          </div>
          <span className="text-xs font-bold text-amber-800">4.8/5 · 1,200+ reviews</span>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {reviews.slice(0, 6).map((r, idx) => (
          <div
            key={idx}
            data-testid={`testimonial-${idx}`}
            className="bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-xl transition-all relative"
          >
            <Quote size={18} className="text-[#D4AF37] absolute top-4 right-4 opacity-40" />
            <div className="flex items-center gap-1 text-amber-500">
              {Array.from({ length: r.rating }).map((_, j) => <Star key={j} size={13} fill="currentColor" />)}
            </div>
            <p className="mt-3 text-sm text-slate-700 leading-relaxed">
              "{renderTextWithHighlight(r.text, r.highlight)}"
            </p>
            <div className="mt-4 flex items-center gap-3 pt-4 border-t border-slate-100">
              <img
                src={avatarUrl(r.name + (r.city || ""))}
                alt={r.name}
                className="h-10 w-10 rounded-full object-cover border border-slate-100 shrink-0"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1">
                  <p className="text-sm font-bold text-[#0A2540] truncate">{r.name}</p>
                  <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-full" data-testid={`verified-user-${idx}`}>
                    <BadgeCheck size={10} /> Verified User
                  </span>
                </div>
                <p className="text-[11px] text-slate-500">{r.city ? `${r.city} · ` : ""}{r.topic}</p>
              </div>
              {r.date && <span className="text-[10px] text-slate-400 font-semibold">{r.date}</span>}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
